import { ShieldAlert, Phone, Mail, BadgeCheck, MapPin } from "lucide-react";
import { usDate } from "@/lib/dates";
import { FormSendActions, SEND_STATUS } from "@/components/forms/FormSendActions";
import { studentAge } from "@/components/StudentPhotoPicker";
import { Section, InfoRow, Stat, Empty, Row, EventChip, fmtDT, fmtD } from "./ProfileBits";
import { formatPhone } from "@/components/PhoneInput";

export function StudentOverview({ p }) {
  const s = p.student;
  const contacts = s.authorized_pickups || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="student-profile-overview">
      <Section title="Student details">
        <InfoRow label="Full name" value={s.full_name} testid="profile-name" />
        <InfoRow label="Grade / class" value={p.class_name || s.grade} testid="profile-grade" />
        <InfoRow label="Teacher" value={p.teacher_name} />
        {(p.period_events || []).length > 0 && (
          <div className="px-4 py-2.5 border-b border-slate-50" data-testid="profile-period-events">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Period check-ins · last 7 days</div>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {p.period_events.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs" data-testid={`period-event-${e.id}`}>
                  <span className={`w-9 shrink-0 rounded px-1 py-0.5 text-center font-semibold ${e.event_type === "period_in" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{e.event_type === "period_in" ? "IN" : "OUT"}</span>
                  <span className="font-medium text-slate-900 truncate">{e.period} · {e.class_name}</span>
                  {e.late && <span className="rounded bg-amber-100 px-1 text-amber-800 font-semibold">Late</span>}
                  <span className="ml-auto shrink-0 text-slate-500">{new Date(e.occurred_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(p.schedule || []).length > 0 && (
          <div className="px-4 py-2.5 border-b border-slate-50" data-testid="profile-schedule">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Class schedule</div>
            <div className="space-y-1">
              {p.schedule.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm" data-testid={`profile-schedule-${c.id}`}>
                  <span className="w-20 shrink-0 text-xs font-semibold text-primary">{c.homeroom ? "Homeroom" : c.period || "—"}</span>
                  <span className="font-medium text-slate-900 truncate">{c.name}</span>
                  <span className="text-slate-500 truncate">{c.teacher_name}</span>
                  {c.room && <span className="ml-auto text-xs text-slate-400 shrink-0">Rm {c.room}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        <InfoRow label="Date of birth" value={s.date_of_birth ? `${new Date(s.date_of_birth + "T00:00:00").toLocaleDateString([], { dateStyle: "medium" })}${studentAge(s.date_of_birth) ? ` · ${studentAge(s.date_of_birth)}` : ""}` : ""} testid="profile-dob" />
        <InfoRow label="Home address" value={s.address ? <span className="inline-flex items-center gap-1.5">{s.address}{s.address_verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" title="Verified with Google" data-testid="profile-address-verified" />}</span> : ""} testid="profile-address" />
        <InfoRow label="Rider type" value={s.rider_type === "bus" ? `School bus ${p.bus_number ? `#${p.bus_number}` : ""}` : "Car rider"} testid="profile-rider" />
        {s.rider_type === "bus" ? <InfoRow label="Bus driver" value={p.bus_driver} /> : <InfoRow label="Car number" value={s.car_number} testid="profile-carnum" />}
        <InfoRow label="Status" value={s.active === false ? "Archived" : "Active"} />
        <InfoRow label="Student ID" value={s.external_id} />
      </Section>
      <Section title={`Authorized pickups (${contacts.length})`} testid="profile-pickups">
        {contacts.length === 0 && <Empty text="No authorized pickups on file." />}
        {contacts.map((c, i) => (
          <div key={i} className="px-4 py-2.5 border-b border-slate-50 last:border-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">{c.name}</span>
              <span className="text-[11px] uppercase tracking-wider text-slate-400">{c.relationship}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
              {c.license_number && <span>DL {c.license_number}</span>}
              {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {formatPhone(c.phone) || c.phone}</span>}
              {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
              {c.address && <span className="inline-flex items-center gap-1 basis-full" data-testid={`pickup-address-${i}`}><MapPin className="h-3 w-3" /> {c.address}{c.address_verified && <BadgeCheck className="h-3 w-3 text-emerald-600" title="Verified with Google" />}</span>}
            </div>
          </div>
        ))}
      </Section>
      <Section title={`Parent app accounts (${p.parents.length})`} testid="profile-parents">
        {p.parents.length === 0 && <Empty text="No parent has joined the parent app yet." />}
        {p.parents.map((pa) => (
          <Row key={pa.id}>
            <span className="font-medium text-slate-900 flex-1">{pa.name || pa.phone}</span>
            <span className="text-xs text-slate-500">{pa.phone}</span>
            <span className={`text-[11px] font-semibold uppercase ${pa.status === "active" ? "text-emerald-700" : "text-amber-700"}`}>{pa.status}</span>
          </Row>
        ))}
      </Section>
      <Section title="Right now" testid="profile-live">
        <div className="px-4 py-4 text-sm">
          {p.live_status ? <><EventChip type={p.live_status} /> <span className="text-slate-500 ml-2">{fmtDT(p.events[0]?.occurred_at)}</span></>
            : <span className="text-slate-400">No activity recorded today.</span>}
        </div>
      </Section>
    </div>
  );
}

export function StudentActivity({ p }) {
  return (
    <div className="space-y-4" data-testid="student-profile-activity">
      <Section title={`Check-in / check-out history (${p.events.length})`}>
        {p.events.length === 0 && <Empty text="No check-ins or pickups recorded yet." />}
        {p.events.map((e) => (
          <Row key={e.id} testid={`profile-event-${e.id}`}>
            <span className="w-40 text-xs text-slate-500">{fmtDT(e.occurred_at)}</span>
            <EventChip type={e.event_type} />
            <span className="flex-1 text-xs text-slate-600">
              {e.picker_name && <>by <b className="text-slate-900">{e.picker_name}</b>{e.picker_license ? ` · DL ${e.picker_license}` : ""}</>}
            </span>
            {e.officer_name && <span className="text-xs text-slate-400">Officer {e.officer_name}</span>}
          </Row>
        ))}
      </Section>
      <Section title={`Rejected pickup attempts (${p.rejections.length})`} testid="profile-rejections">
        {p.rejections.length === 0 && <Empty text="No unauthorized pickup attempts." />}
        {p.rejections.map((r) => (
          <Row key={r.id}>
            <ShieldAlert className="h-4 w-4 text-rose-500" />
            <span className="w-40 text-xs text-slate-500">{fmtDT(r.occurred_at)}</span>
            <span className="flex-1 text-xs text-slate-700"><b>{r.picker_name || "Unknown"}</b>{r.picker_license ? ` · DL ${r.picker_license}` : ""} · {r.reason?.replace(/_/g, " ")}</span>
            {r.officer_name && <span className="text-xs text-slate-400">Officer {r.officer_name}</span>}
          </Row>
        ))}
      </Section>
    </div>
  );
}

export function StudentAttendance({ a }) {
  return (
    <div className="space-y-4" data-testid="student-profile-attendance">
      <div className="text-xs text-slate-500">{fmtD(a.from)} → {fmtD(a.to)}</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="School days" value={a.school_days} />
        <Stat label="Present" value={a.present} tone="text-emerald-700" />
        <Stat label="Tardy" value={a.tardy} tone="text-amber-700" />
        <Stat label="Excused" value={a.absent_excused} tone="text-emerald-700" />
        <Stat label="Unexcused" value={a.absent_unexcused} tone="text-rose-700" testid="profile-unexcused" />
      </div>
      <Section title={`Absences (${a.absences?.length || 0})`}>
        {(!a.absences || a.absences.length === 0) && <Empty text="No absences in this range." />}
        {a.absences?.map((d) => (
          <Row key={d.date}>
            <span className="w-28 font-medium text-slate-900">{usDate(d.date)}</span>
            <span className={`text-[11px] font-semibold uppercase ${d.status === "excused" ? "text-emerald-700" : "text-rose-700"}`}>{d.status}</span>
            <span className="flex-1 text-xs text-slate-500 truncate">{d.note}</span>
          </Row>
        ))}
      </Section>
    </div>
  );
}

export function StudentForms({ forms, readOnly, onChanged }) {
  return (
    <Section title={`Forms sent to parents (${forms.length})`} testid="student-profile-forms">
      {forms.length === 0 && <Empty text="No forms have been sent for this student." />}
      {forms.map((f) => {
        const [label, tone] = SEND_STATUS(f);
        return (
          <Row key={f.id} testid={`profile-form-${f.id}`}>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-slate-900 truncate">{f.form_title}</span>
              {f.correction_note && f.status === "pending" && <span className="block text-[11px] text-orange-700 truncate" data-testid={`profile-form-note-${f.id}`}>Correction requested: {f.correction_note}</span>}
            </span>
            <span className="text-xs text-slate-500">{f.parent_name}</span>
            <span className="text-xs text-slate-400">Sent {fmtD(f.sent_at)}</span>
            <span className={`text-[11px] font-semibold uppercase ${tone}`} data-testid={`profile-form-status-${f.id}`}>
              {label}{f.status === "completed" ? ` ${fmtD(f.second_signed_at || f.signed_at)}` : ""}
            </span>
            {!readOnly && <FormSendActions send={f} onChanged={onChanged} />}
          </Row>
        );
      })}
    </Section>
  );
}
